/**
 * Extended preset icons for the Establishment (52 presets, organised in
 * 6 thematic rows : Santé · Commerce · Culture · Transport · Services · Autre).
 *
 * Layout follows the spec mock provided by the product team — each preset has
 * a short kebab-case `key` (stored on `commerce.icon`), a multi-line `label`
 * (rendered under the icon tile), and an `Icon` component. A few categories
 * have a brand-blue `accent` so the tile mirrors the reference (siren bulb,
 * parking sign, bus, etc. — kept as a simple second-color tint rather than
 * dual-color SVG which lucide doesn't expose).
 *
 * The `icon` field on the commerce document still accepts a `data:` URL
 * (custom upload) — see `isCustomCommerceIcon`.
 */
import {
  Building2, Hospital, Cross, Stethoscope, Syringe, Siren, HeartPulse, Baby,
  Milk, PawPrint,
  Croissant, Cake, Utensils, Coffee, Sandwich, ShoppingCart, ShoppingBag, Tag,
  Scissors, Sparkles,
  HandHeart, Church, MoonStar, Star, Film, Popcorn, Clapperboard, Landmark,
  BookOpen, GraduationCap,
  FileText, Mail, TrainFront, Bus, Plane, Car, Fuel, ParkingSquare,
  ShowerHead, Users, WashingMachine, Hotel, Luggage, Camera, Dumbbell, Flower2, Trees,
} from "lucide-react";

/**
 * One entry per tile. `accent: true` swaps the icon color to brand blue
 * to mirror the mock's selective blue strokes (sirens, parking sign, bus…).
 */
export const COMMERCE_ICON_PRESETS = [
  // Row 1 — Santé / Médical
  { key: "sante-medical", label: "Santé / Médical", Icon: Stethoscope },
  { key: "hopital", label: "Hôpital", Icon: Hospital },
  { key: "pharmacie", label: "Pharmacie", Icon: Cross },
  { key: "dentiste", label: "Dentiste", Icon: Sparkles },
  { key: "laboratoire", label: "Laboratoire Analyses", Icon: Syringe, accent: true },
  { key: "urgences", label: "Urgences", Icon: Siren, accent: true },
  { key: "cardiologie", label: "Cardiologie", Icon: HeartPulse },
  { key: "enfants-famille", label: "Enfants / Famille", Icon: Baby },
  { key: "maternite", label: "Maternité", Icon: Milk },
  { key: "veterinaire", label: "Vétérinaire", Icon: PawPrint },
  // Row 2 — Commerce / Restauration
  { key: "boulangerie", label: "Boulangerie", Icon: Croissant },
  { key: "patisserie", label: "Pâtisserie", Icon: Cake },
  { key: "restaurant", label: "Restaurant", Icon: Utensils },
  { key: "cafe", label: "Café", Icon: Coffee },
  { key: "restauration-rapide", label: "Restauration rapide", Icon: Sandwich },
  { key: "supermarche", label: "Supermarché / Commerce", Icon: ShoppingCart, accent: true },
  { key: "magasin", label: "Magasin", Icon: ShoppingBag },
  { key: "boutique", label: "Boutique", Icon: Tag },
  { key: "coiffeur", label: "Coiffeur", Icon: Scissors, accent: true },
  { key: "esthetique", label: "Esthétique / Beauté", Icon: Sparkles, accent: true },
  // Row 3 — Culte / Culture
  { key: "lieu-culte", label: "Lieu de culte", Icon: HandHeart },
  { key: "eglise", label: "Église", Icon: Church },
  { key: "mosquee", label: "Mosquée", Icon: MoonStar, accent: true },
  { key: "synagogue", label: "Synagogue", Icon: Star, accent: true },
  { key: "cinema", label: "Cinéma", Icon: Film, accent: true },
  { key: "cinema-multiplex", label: "Cinéma / Multiplex", Icon: Popcorn },
  { key: "theatre", label: "Théâtre / Spectacle", Icon: Clapperboard },
  { key: "musee", label: "Musée / Galerie", Icon: Landmark },
  { key: "bibliotheque", label: "Bibliothèque", Icon: BookOpen },
  { key: "education", label: "Éducation / École", Icon: GraduationCap },
  // Row 4 — Administration / Transport
  { key: "administration", label: "Administration publique", Icon: Landmark },
  { key: "mairie", label: "Mairie", Icon: Landmark },
  { key: "bureau-services", label: "Bureau / Services", Icon: FileText },
  { key: "poste", label: "Poste", Icon: Mail },
  { key: "gare-train", label: "Gare / Train", Icon: TrainFront, accent: true },
  { key: "bus-transport", label: "Bus / Transport", Icon: Bus, accent: true },
  { key: "aeroport", label: "Aéroport", Icon: Plane },
  { key: "taxi", label: "Taxi", Icon: Car, accent: true },
  { key: "station-service", label: "Station-service", Icon: Fuel, accent: true },
  { key: "parking", label: "Parking", Icon: ParkingSquare, accent: true },
  // Row 5 — Services
  { key: "douches-sanitaires", label: "Douches / Sanitaires", Icon: ShowerHead },
  { key: "toilettes", label: "Toilettes", Icon: Users, accent: true },
  { key: "laverie", label: "Laverie", Icon: WashingMachine },
  { key: "hotel", label: "Hôtel", Icon: Hotel },
  { key: "tourisme-voyage", label: "Tourisme / Voyage", Icon: Luggage },
  { key: "photo", label: "Photo", Icon: Camera },
  { key: "sport-fitness", label: "Salle de sport / Fitness", Icon: Dumbbell },
  { key: "bien-etre-spa", label: "Bien-être / Spa", Icon: Flower2 },
  { key: "parc-loisirs", label: "Parc / Loisirs", Icon: Trees },
  // Legacy generic — kept for backwards compatibility with existing data.
  { key: "etablissement", label: "Établissement", Icon: Building2 },
];

const PRESET_MAP = COMMERCE_ICON_PRESETS.reduce((acc, p) => { acc[p.key] = p.Icon; return acc; }, {});
const PRESET_META = COMMERCE_ICON_PRESETS.reduce((acc, p) => { acc[p.key] = p; return acc; }, {});

export function isCustomCommerceIcon(value) {
  return typeof value === "string" && value.startsWith("data:");
}

export function getCommerceIcon(value) {
  if (!value || isCustomCommerceIcon(value)) return null;
  return PRESET_MAP[value] || null;
}

/** Whether the preset is rendered with the brand-blue accent color. */
export function isCommerceIconAccented(value) {
  if (!value || isCustomCommerceIcon(value)) return false;
  return !!(PRESET_META[value] && PRESET_META[value].accent);
}
